import React from 'react';
import focusableSelectors from '../../packages/focusable-selectors/index.js';
import { useIsFirstRender } from '../use-is-first-render/use-is-first-render.js';

/** Handles the tab key when focus trap is set on a given container element.
 *
 * @since 8.8.0
 * @param {boolean} active - Whether the the container is active/visible or not.
 * @param {HTMLElement | null} container - The container element to set the focus trap on.
 * @param {HTMLElement | null} target - The target element to return focus to when the container is
 * closed.
 * @param {boolean} skipInitialFocus - Whether to skip setting the initial focus when container is
 * opened. E.g.: if the component handles it on its own, like the SubMenu that can set the initial
 * focus on the last element.
 * @private This hook is private because it is very specific to our internal use cases and its API
 * is not stable enough for public use.
 */
function useTabTrap(active, container, target, skipInitialFocus) {
  const isFirstRender = useIsFirstRender();
  const [firstInteractiveElement, setFirstInteractiveElement] = React.useState();
  const [lastInteractiveElement, setLastInteractiveElement] = React.useState();
  /* Set focus to target element when popup is closed, but is not the first render. */
  React.useEffect(() => {
    if (!active && isFirstRender === false) {
      target === null || target === void 0 ? void 0 : target.focus();
    }
  }, [active]);
  /* Set focus to first interactive element when popup is opened and finishes loading. */
  React.useEffect(() => {
    if (active && firstInteractiveElement && !skipInitialFocus) {
      firstInteractiveElement.focus();
    }
  }, [active, firstInteractiveElement, skipInitialFocus]);
  /* When component is active and not longer loading,
   * find the first and last interactive elements and store them. */
  React.useEffect(() => {
    if (active && container) {
      const allInteractiveElements = container.querySelectorAll(focusableSelectors.join(','));
      const firstElement = allInteractiveElements === null || allInteractiveElements === void 0 ? void 0 : allInteractiveElements.item(0);
      const lastElement = allInteractiveElements === null || allInteractiveElements === void 0 ? void 0 : allInteractiveElements.item(allInteractiveElements.length - 1);
      setFirstInteractiveElement(firstElement);
      setLastInteractiveElement(lastElement);
    }
  }, [active, container]);
  const handleTabKeyDown = React.useCallback(event => {
    const {
      target: eventTarget
    } = event;
    if (event.key !== 'Tab' || eventTarget !== firstInteractiveElement && eventTarget !== lastInteractiveElement) {
      return;
    }
    if (eventTarget === firstInteractiveElement && event.shiftKey) {
      event.preventDefault();
      lastInteractiveElement === null || lastInteractiveElement === void 0 ? void 0 : lastInteractiveElement.focus();
    }
    if (eventTarget === lastInteractiveElement && !event.shiftKey) {
      event.preventDefault();
      firstInteractiveElement === null || firstInteractiveElement === void 0 ? void 0 : firstInteractiveElement.focus();
    }
  }, [firstInteractiveElement, lastInteractiveElement]);
  return {
    handleTabKeyDown,
    firstInteractiveElement,
    lastInteractiveElement
  };
}

export { useTabTrap };
